import React, { useState, useEffect } from "react";
import {
  Users,
  Search,
  Download,
  Filter,
  Edit,
  Trash2,
  ChevronRight,
  TrendingUp,
  Calendar,
  LayoutGrid,
  List,
  ArrowUpDown,
  AlertCircle,
  Gift,
  Copy,
  Check
} from "lucide-react";
import { getPayrollEmployeesApi, runPayrollApi, getMonthlyBonusApi, deleteBonusApi, getEmployeesApi, FILE_BASE_URL } from "../../app/api";
import PayrollKPIs from "./components/PayrollKPIs";
import EditPayrollInfoModal from "./components/EditPayrollInfoModal";
import BonusModal from "./components/BonusModal";
import { toast } from "react-hot-toast";
import moment from "moment";

const formatCurrency = (amount) => {
  const value = parseFloat(amount);
  return isNaN(value) ? "0" : value.toLocaleString('en-IN');
};

const escapeXml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

const crcTable = Array.from({ length: 256 }, (_, index) => {
  let crc = index;
  for (let bit = 0; bit < 8; bit += 1) {
    crc = crc & 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1;
  }
  return crc >>> 0;
});

const crc32 = (bytes) => {
  let crc = 0xffffffff;
  bytes.forEach((byte) => {
    crc = crcTable[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  });
  return (crc ^ 0xffffffff) >>> 0;
};

const pushUint16 = (target, value) => {
  target.push(value & 0xff, (value >>> 8) & 0xff);
};

const pushUint32 = (target, value) => {
  target.push(value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, (value >>> 24) & 0xff);
};

const getDosDateTime = () => {
  const date = new Date();
  const time = (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2);
  const dosDate = ((date.getFullYear() - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate();
  return { time, date: dosDate };
};

const createZipBlob = (files, type) => {
  const encoder = new TextEncoder();
  const localParts = [];
  const centralParts = [];
  let offset = 0;
  const { time, date } = getDosDateTime();

  files.forEach(({ name, content }) => {
    const nameBytes = encoder.encode(name);
    const dataBytes = encoder.encode(content);
    const checksum = crc32(dataBytes);
    const localHeader = [];

    pushUint32(localHeader, 0x04034b50);
    pushUint16(localHeader, 20);
    pushUint16(localHeader, 0);
    pushUint16(localHeader, 0);
    pushUint16(localHeader, time);
    pushUint16(localHeader, date);
    pushUint32(localHeader, checksum);
    pushUint32(localHeader, dataBytes.length);
    pushUint32(localHeader, dataBytes.length);
    pushUint16(localHeader, nameBytes.length);
    pushUint16(localHeader, 0);
    localParts.push(new Uint8Array(localHeader), nameBytes, dataBytes);

    const centralHeader = [];
    pushUint32(centralHeader, 0x02014b50);
    pushUint16(centralHeader, 20);
    pushUint16(centralHeader, 20);
    pushUint16(centralHeader, 0);
    pushUint16(centralHeader, 0);
    pushUint16(centralHeader, time);
    pushUint16(centralHeader, date);
    pushUint32(centralHeader, checksum);
    pushUint32(centralHeader, dataBytes.length);
    pushUint32(centralHeader, dataBytes.length);
    pushUint16(centralHeader, nameBytes.length);
    pushUint16(centralHeader, 0);
    pushUint16(centralHeader, 0);
    pushUint16(centralHeader, 0);
    pushUint16(centralHeader, 0);
    pushUint32(centralHeader, 0);
    pushUint32(centralHeader, offset);
    centralParts.push(new Uint8Array(centralHeader), nameBytes);

    offset += localHeader.length + nameBytes.length + dataBytes.length;
  });

  const centralSize = centralParts.reduce((sum, part) => sum + part.length, 0);
  const endRecord = [];
  pushUint32(endRecord, 0x06054b50);
  pushUint16(endRecord, 0);
  pushUint16(endRecord, 0);
  pushUint16(endRecord, files.length);
  pushUint16(endRecord, files.length);
  pushUint32(endRecord, centralSize);
  pushUint32(endRecord, offset);
  pushUint16(endRecord, 0);

  return new Blob([...localParts, ...centralParts, new Uint8Array(endRecord)], { type });
};

const columnName = (index) => {
  let name = "";
  let current = index;
  while (current > 0) {
    const remainder = (current - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    current = Math.floor((current - 1) / 26);
  }
  return name;
};

const xlsxCell = (rowIndex, columnIndex, value, style = 0, type = "text") => {
  const ref = `${columnName(columnIndex)}${rowIndex}`;
  const styleAttr = style ? ` s="${style}"` : "";
  if (type === "number") {
    return `<c r="${ref}"${styleAttr}><v>${Number(value) || 0}</v></c>`;
  }
  if (value === undefined || value === null || value === "") {
    return `<c r="${ref}"${styleAttr}/>`;
  }
  return `<c r="${ref}"${styleAttr} t="inlineStr"><is><t>${escapeXml(value)}</t></is></c>`;
};

const xlsxRow = (rowIndex, cells) =>
  `<row r="${rowIndex}">${cells.map((cell, index) =>
    xlsxCell(rowIndex, index + 1, cell.value, cell.style, cell.type)
  ).join("")}</row>`;

const createSalaryWorkbookBlob = ({ employees, monthLabel, generatedAt, getBonus }) => {
  const headers = ["S.No.", "ID", "Employee", "Bank", "Account Name", "Account No.", "IFSC", "PAN", "Base Salary", "Bonus", "Total Pay"];
  const totalSalary = employees.reduce((sum, emp) => sum + (Number(emp.salary) || 0), 0);
  const totalBonus = employees.reduce((sum, emp) => sum + (Number(getBonus(emp)) || 0), 0);
  const employeeRows = employees.map((emp, index) => {
    const salary = Number(emp.salary) || 0;
    const bonus = Number(getBonus(emp)) || 0;
    return xlsxRow(index + 7, [
      { value: index + 1, style: 6, type: "number" },
      { value: emp.employee_id || "", style: 6 },
      { value: emp.name || "Unknown", style: 7 },
      { value: emp.bank_name || "Not Set" },
      { value: emp.account_name || "Not Set" },
      { value: emp.account_number || "Not Set", style: 8 },
      { value: emp.ifsc_code || "N/A", style: 8 },
      { value: emp.pan_number || "N/A", style: 8 },
      { value: salary, style: 4, type: "number" },
      { value: bonus, style: 4, type: "number" },
      { value: salary + bonus, style: 4, type: "number" }
    ]);
  }).join("");

  const worksheet = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
    <worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
      <cols>
        <col min="1" max="1" width="8" customWidth="1"/>
        <col min="2" max="2" width="10" customWidth="1"/>
        <col min="3" max="3" width="24" customWidth="1"/>
        <col min="4" max="4" width="22" customWidth="1"/>
        <col min="5" max="5" width="24" customWidth="1"/>
        <col min="6" max="6" width="22" customWidth="1"/>
        <col min="7" max="7" width="18" customWidth="1"/>
        <col min="8" max="8" width="18" customWidth="1"/>
        <col min="9" max="11" width="15" customWidth="1"/>
      </cols>
      <sheetData>
        ${xlsxRow(1, [{ value: "Employee Salary Report", style: 1 }])}
        ${xlsxRow(2, [{ value: `Month: ${monthLabel} | Employees: ${employees.length} | Generated: ${generatedAt}`, style: 2 }])}
        ${xlsxRow(3, [{}])}
        ${xlsxRow(4, [
          { value: "Total Base Salary", style: 3 }, {}, { value: totalSalary, style: 5, type: "number" },
          { value: "Total Bonus", style: 3 }, {}, { value: totalBonus, style: 5, type: "number" },
          { value: "Grand Total", style: 3 }, {}, { value: totalSalary + totalBonus, style: 5, type: "number" }
        ])}
        ${xlsxRow(5, [{}])}
        ${xlsxRow(6, headers.map((header) => ({ value: header, style: 3 })))}
        ${employeeRows}
      </sheetData>
      <mergeCells count="5">
        <mergeCell ref="A1:K1"/>
        <mergeCell ref="A2:K2"/>
        <mergeCell ref="A4:B4"/>
        <mergeCell ref="D4:E4"/>
        <mergeCell ref="G4:H4"/>
      </mergeCells>
    </worksheet>`;

  const styles = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
    <styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
      <numFmts count="1"><numFmt numFmtId="164" formatCode="₹#,##0"/></numFmts>
      <fonts count="5">
        <font><sz val="11"/><name val="Arial"/></font>
        <font><b/><sz val="20"/><color rgb="FFFFFFFF"/><name val="Arial"/></font>
        <font><sz val="10"/><color rgb="FF4D426C"/><name val="Arial"/></font>
        <font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Arial"/></font>
        <font><b/><sz val="11"/><color rgb="FF0A7D45"/><name val="Arial"/></font>
      </fonts>
      <fills count="5">
        <fill><patternFill patternType="none"/></fill>
        <fill><patternFill patternType="gray125"/></fill>
        <fill><patternFill patternType="solid"><fgColor rgb="FF12082F"/><bgColor indexed="64"/></patternFill></fill>
        <fill><patternFill patternType="solid"><fgColor rgb="FFEEEAF8"/><bgColor indexed="64"/></patternFill></fill>
        <fill><patternFill patternType="solid"><fgColor rgb="FF24164D"/><bgColor indexed="64"/></patternFill></fill>
      </fills>
      <borders count="2">
        <border><left/><right/><top/><bottom/><diagonal/></border>
        <border><left style="thin"><color rgb="FFDED9EC"/></left><right style="thin"><color rgb="FFDED9EC"/></right><top style="thin"><color rgb="FFDED9EC"/></top><bottom style="thin"><color rgb="FFDED9EC"/></bottom><diagonal/></border>
      </borders>
      <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
      <cellXfs count="9">
        <xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0"/>
        <xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1"/>
        <xf numFmtId="0" fontId="2" fillId="3" borderId="1" xfId="0" applyFont="1" applyFill="1"/>
        <xf numFmtId="0" fontId="3" fillId="4" borderId="1" xfId="0" applyFont="1" applyFill="1"/>
        <xf numFmtId="164" fontId="4" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyAlignment="1"><alignment horizontal="right"/></xf>
        <xf numFmtId="164" fontId="4" fillId="3" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyAlignment="1"><alignment horizontal="right"/></xf>
        <xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyAlignment="1"><alignment horizontal="center"/></xf>
        <xf numFmtId="0" fontId="3" fillId="0" borderId="1" xfId="0" applyFont="1"/>
        <xf numFmtId="49" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1"/>
      </cellXfs>
      <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
    </styleSheet>`;

  const files = [
    {
      name: "[Content_Types].xml",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>`
    },
    {
      name: "_rels/.rels",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`
    },
    {
      name: "xl/workbook.xml",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="Employee Salaries" sheetId="1" r:id="rId1"/></sheets></workbook>`
    },
    {
      name: "xl/_rels/workbook.xml.rels",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`
    },
    { name: "xl/styles.xml", content: styles },
    { name: "xl/worksheets/sheet1.xml", content: worksheet }
  ];

  return createZipBlob(files, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
};

const normalizeFlag = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  const normalized = String(value).trim().toLowerCase();
  if (["true", "1", "yes", "y", "active"].includes(normalized)) return true;
  if (["false", "0", "no", "n", "inactive"].includes(normalized)) return false;
  return null;
};

const getPayrollField = (emp, keys) => {
  for (const key of keys) {
    const value = key.split(".").reduce((obj, part) => obj?.[part], emp);
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return "";
};

const getProfileImageUrl = (emp = {}) => {
  const path = getPayrollField(emp, [
    "profile_pic",
    "profilePic",
    "profile_image",
    "profileImage",
    "avatar",
    "image",
    "user.profile_pic",
    "employee.profile_pic"
  ]);

  if (!path) return "";
  const cleanPath = String(path).trim();
  if (/^https?:\/\//i.test(cleanPath)) return cleanPath;
  return `${FILE_BASE_URL}${cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`}`;
};

const getEmployeeFullName = (emp = {}) =>
  String(
    emp.name ||
    emp.employee_name ||
    emp.full_name ||
    `${emp.first_name || ""} ${emp.last_name || ""}`.trim()
  ).trim();

const getEmployeeIdentityValues = (emp = {}) => [
  emp.id,
  emp.user_id,
  emp.employee_id,
  emp.emp_id,
  emp.employee_salary_id,
  emp.roll_number,
  emp.roll_no,
  emp.employee_code,
  emp.emp_code
].filter((value) => value !== undefined && value !== null && String(value).trim() !== "");

const buildEmployeeLookup = (users = []) => {
  const lookup = new Map();
  users.forEach((user) => {
    getEmployeeIdentityValues(user).forEach((value) => {
      lookup.set(String(value), user);
    });

    const name = getEmployeeFullName(user).toLowerCase();
    if (name) lookup.set(`name:${name}`, user);
  });
  return lookup;
};

const findMatchingEmployee = (payrollEmployee, lookup) => {
  for (const value of getEmployeeIdentityValues(payrollEmployee)) {
    const match = lookup.get(String(value));
    if (match) return match;
  }

  const name = getEmployeeFullName(payrollEmployee).toLowerCase();
  return name ? lookup.get(`name:${name}`) : null;
};

const mergePayrollWithEmployeeProfiles = (payrollEmployees = [], users = []) => {
  const lookup = buildEmployeeLookup(users);
  return payrollEmployees.map((payrollEmployee) => {
    const matchedUser = findMatchingEmployee(payrollEmployee, lookup);
    if (!matchedUser) return { ...payrollEmployee, __profileMatch: false };

    return {
      ...matchedUser,
      ...payrollEmployee,
      __profileMatch: true,
      profile_pic: payrollEmployee.profile_pic || matchedUser.profile_pic,
      profilePic: payrollEmployee.profilePic || matchedUser.profilePic,
      profile_image: payrollEmployee.profile_image || matchedUser.profile_image,
      user: {
        ...(matchedUser.user || {}),
        ...(payrollEmployee.user || {}),
        profile_pic: payrollEmployee.user?.profile_pic || matchedUser.user?.profile_pic || matchedUser.profile_pic,
        is_active: payrollEmployee.user?.is_active ?? matchedUser.user?.is_active ?? matchedUser.is_active,
        deleted_at: payrollEmployee.user?.deleted_at || matchedUser.user?.deleted_at || matchedUser.deleted_at
      },
      employee: {
        ...(matchedUser.employee || {}),
        ...(payrollEmployee.employee || {}),
        profile_pic: payrollEmployee.employee?.profile_pic || matchedUser.employee?.profile_pic || matchedUser.profile_pic,
        is_active: payrollEmployee.employee?.is_active ?? matchedUser.employee?.is_active ?? matchedUser.is_active,
        deleted_at: payrollEmployee.employee?.deleted_at || matchedUser.employee?.deleted_at || matchedUser.deleted_at
      }
    };
  });
};

const isCurrentCompanyEmployee = (emp = {}) => {
  const status = String(getPayrollField(emp, [
    "employment_status",
    "employee_status",
    "user_status",
    "status",
    "user.status",
    "employee.status"
  ])).trim().toLowerCase();

  const activeFlag = normalizeFlag(getPayrollField(emp, [
    "is_active",
    "active",
    "isActive",
    "user.is_active",
    "employee.is_active"
  ]));

  const deletedFlag = normalizeFlag(getPayrollField(emp, [
    "is_deleted",
    "deleted",
    "isDeleted",
    "user.is_deleted",
    "employee.is_deleted"
  ]));

  const exEmployeeFlag = normalizeFlag(getPayrollField(emp, [
    "is_ex_employee",
    "ex_employee",
    "isExEmployee",
    "is_ex",
    "user.is_ex_employee",
    "employee.is_ex_employee"
  ]));

  const exitDate = getPayrollField(emp, [
    "exit_date",
    "deleted_at",
    "left_at",
    "leaving_date",
    "last_working_day",
    "resigned_at",
    "terminated_at",
    "relieved_at",
    "user.exit_date",
    "user.deleted_at",
    "employee.exit_date",
    "employee.deleted_at"
  ]);

  const inactiveStatuses = [
    "inactive",
    "deleted",
    "ex",
    "ex employee",
    "ex-employee",
    "former",
    "left",
    "resigned",
    "terminated",
    "relieved",
    "archived"
  ];

  if (activeFlag === false || deletedFlag === true || exEmployeeFlag === true || exitDate) return false;
  if (inactiveStatuses.includes(status)) return false;
  return true;
};

const EmployeeSalaries = () => {
  const [selectedMonth, setSelectedMonth] = useState(moment().subtract(1, 'month').format("YYYY-MM")); // YYYY-MM
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [viewType, setViewType] = useState("list"); // grid or list
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [showExistsModal, setShowExistsModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [bonuses, setBonuses] = useState([]);
  const [bonusModalOpen, setBonusModalOpen] = useState(false);
  const [selectedBonusEmployee, setSelectedBonusEmployee] = useState(null);
  const [viewMode, setViewMode] = useState("salaries");
  const [currentPage, setCurrentPage] = useState(1);
  const [copiedKey, setCopiedKey] = useState("");
  const itemsPerPage = 8;

  useEffect(() => {
    fetchEmployees();
  }, [selectedMonth, refreshTrigger]);



  const Tooltip = ({ text, children }) => {
    return (
      <div className="relative group flex items-center justify-center">
        {children}

        <div className="
        absolute bottom-full mb-2
        px-2 py-1 text-xs font-medium
        text-white bg-black rounded-md
        whitespace-nowrap
        opacity-0 scale-95
        group-hover:opacity-100 group-hover:scale-100
        transition-all duration-100
        pointer-events-none
      ">
          {text}
        </div>
      </div>
    );
  };





  const handleRunPayroll = async () => {
    try {
      setIsRunning(true);
      const res = await runPayrollApi({ month: selectedMonth });
      if (res.success) {
        toast.success("Payroll processed successfully!");
        setRefreshTrigger(prev => prev + 1);
      }
    } catch (error) {
      console.error("Error running payroll:", error);
      setShowExistsModal(true);
    } finally {
      setIsRunning(false);
    }
  };

  const handleDeleteBonus = async (bonusId) => {
    if (!window.confirm("Are you sure you want to delete this bonus?")) return;
    try {
      const res = await deleteBonusApi(bonusId);
      if (res.success) {
        toast.success("Bonus deleted successfully!");
        setRefreshTrigger(prev => prev + 1);
      } else {
        toast.error(res.message || "Failed to delete bonus");
      }
    } catch (error) {
      console.error("Error deleting bonus:", error);
      toast.error("An error occurred while deleting");
    }
  };

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const [empRes, bonusRes, usersRes] = await Promise.all([
        getPayrollEmployeesApi(selectedMonth),
        getMonthlyBonusApi(selectedMonth),
        getEmployeesApi()
      ]);

      if (empRes.success) {
        const payrollEmployees = empRes.data || [];
        const activeUsers = usersRes.success ? (usersRes.data || []) : [];
        const mergedEmployees = mergePayrollWithEmployeeProfiles(payrollEmployees, activeUsers);
        setEmployees(
          mergedEmployees.filter((emp) =>
            (!usersRes.success || emp.__profileMatch) && isCurrentCompanyEmployee(emp)
          )
        );
      } else {
        toast.error(empRes.message || "Failed to fetch employee salaries");
      }

      if (bonusRes.success) {
        setBonuses(bonusRes.data);
      }
    } catch (error) {
      console.error("Error fetching salaries/bonuses:", error);
      toast.error("An error occurred while fetching data");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (label, value, key) => {
    const text = String(value || "").trim();
    if (!text || text.toLowerCase() === "n/a" || text.toLowerCase() === "not set") return;

    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast.success(`${label} copied`);
      setTimeout(() => setCopiedKey(""), 1200);
    } catch (error) {
      console.error("Clipboard copy failed:", error);
      toast.error(`Unable to copy ${label}`);
    }
  };

  const CopyableValue = ({ label, value, copyKey, className = "" }) => {
    const text = String(value || "").trim();
    const canCopy = text && text.toLowerCase() !== "n/a" && text.toLowerCase() !== "not set";

    return (
      <span className={`inline-flex items-center gap-1.5 min-w-0 ${className}`}>
        <span className="truncate">{canCopy ? text : "N/A"}</span>
        {canCopy && (
          <button
            type="button"
            onClick={() => handleCopy(label, text, copyKey)}
            className="shrink-0 p-1 rounded-md text-white/45 hover:text-cyan-300 hover:bg-cyan-400/10 transition"
            title={`Copy ${label}`}
            aria-label={`Copy ${label}`}
          >
            {copiedKey === copyKey ? <Check size={12} /> : <Copy size={12} />}
          </button>
        )}
      </span>
    );
  };

  const EmployeeAvatar = ({ emp, sizeClass = "w-8 h-8", textClass = "text-[12px]" }) => {
    const imageUrl = getProfileImageUrl(emp);
    const initial = (emp.name || "U").trim()[0]?.toUpperCase() || "U";

    return (
      <div className={`${sizeClass} rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center ${textClass} font-bold overflow-hidden border border-white/10 shrink-0`}>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={`${emp.name || "Employee"} profile`}
            className="w-full h-full object-cover"
            onError={(event) => {
              event.currentTarget.style.display = "none";
              const fallback = event.currentTarget.nextElementSibling;
              if (fallback) fallback.style.display = "flex";
            }}
          />
        ) : null}
        <span className={`${imageUrl ? "hidden" : "flex"} w-full h-full items-center justify-center`}>
          {initial}
        </span>
      </div>
    );
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedEmployees = [...employees].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const filteredEmployees = sortedEmployees.filter(emp =>
    (emp.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (emp.employee_id || "").toString().includes(searchTerm)
  );

  const getEmployeeBonus = (emp) =>
    bonuses.find(b => b.name === emp.name)?.monthly_amount || 0;

  const handleDownloadXls = () => {
    if (!filteredEmployees.length) {
      toast.error("No salary records to download");
      return;
    }

    const monthLabel = moment(selectedMonth).format("MMMM YYYY");
    const fileMonth = moment(selectedMonth).format("MMMM_YYYY");
    const generatedAt = moment().format("DD MMM YYYY, hh:mm A");

    const blob = createSalaryWorkbookBlob({
      employees: filteredEmployees,
      monthLabel,
      generatedAt,
      getBonus: getEmployeeBonus
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Employee_Salaries_${fileMonth}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success("Excel salary report downloaded");
  };

  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentEmployees = filteredEmployees.slice(indexOfFirstItem, indexOfLastItem);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedMonth, viewMode]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#12082F] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#12082F] text-white p-4 sm:p-6 font-sans">
      <div className="max-w-[1600px] mx-auto space-y-6">

        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2 text-white">
              EMPLOYEE SALARIES <span className="text-white/70 font-normal">| Payroll</span>
            </h1>
            <p className="text-white/70 text-[11px] font-medium tracking-widest uppercase mt-0.5">
              Manage your team's compensation and banking info
            </p>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              max={moment().subtract(1, 'month').format("YYYY-MM")}
              className="bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-sm text-white focus:outline-none focus:border-purple-500/50 transition cursor-pointer"
            />
            {/* <button
              onClick={handleRunPayroll}
              disabled={isRunning}
              className="bg-gradient-to-tr from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white px-6 py-2 rounded-xl font-bold text-sm shadow-xl shadow-purple-500/20 transition transform active:scale-95 disabled:opacity-50 disabled:scale-100 flex items-center gap-2"
            >
              {isRunning ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <TrendingUp size={18} />}
              {isRunning ? "Processing..." : "Run Payroll"}
            </button> */}
          </div>
        </div>

        {/* SUMMARY STATS */}
        {/* <PayrollKPIs
          month={selectedMonth}
          refreshTrigger={refreshTrigger}
        /> */}

        {/* SEARCH AND FILTERS */}
        <div className="bg-white/5 border border-white/10 p-2 rounded-[22px] flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1 group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50 group-focus-within:text-purple-400 transition-colors" size={18} />
            <input
              type="text"
              placeholder="Search employees by name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-[18px] pl-12 pr-4 py-3 text-sm text-white focus:outline-none focus:border-purple-500/30 focus:bg-white/[0.08] transition-all"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="flex gap-2">
              <button
                onClick={() => setViewType("list")}
                className={`p-3 rounded-[18px] transition ${viewType === "list" ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' : 'bg-white/5 text-white/70 hover:bg-white/10 border border-transparent'}`}
                title="List View"
              >
                <List size={20} />
              </button>
              <button
                onClick={() => setViewType("grid")}
                className={`p-3 rounded-[18px] transition ${viewType === "grid" ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' : 'bg-white/5 text-white/70 hover:bg-white/10 border border-transparent'}`}
                title="Grid View"
              >
                <LayoutGrid size={20} />
              </button>
            </div>
            <div className="w-px bg-white/10 mx-1 hidden sm:block" />
            {viewMode === "salaries" && (
              <button
                onClick={handleDownloadXls}
                disabled={!filteredEmployees.length}
                className="flex items-center gap-2 px-5 border rounded-[18px] text-sm font-bold transition whitespace-nowrap min-w-fit bg-emerald-500/10 hover:bg-emerald-500/20 border-emerald-400/25 text-emerald-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-emerald-500/10"
                title="Download salary report in Excel format"
              >
                <Download size={18} />
                XLS
              </button>
            )}
            <button
              onClick={() => setViewMode(viewMode === "salaries" ? "bonuses" : "salaries")}
              className={`flex items-center gap-2 px-6 border rounded-[18px] text-sm font-bold transition whitespace-nowrap min-w-fit ${viewMode === "bonuses" ? 'bg-purple-500 border-purple-400 text-white' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/60'}`}
            >
              <Gift size={18} />
              {viewMode === "salaries" ? "View Bonus" : "View Salaries"}
            </button>
          </div>
        </div>

        {viewMode === "salaries" && (
          filteredEmployees.length > 0 ? (
            viewType === "list" ? (
              <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[1100px]">
                  <thead>
                    <tr className="border-b border-white/10 bg-white/5">
                      <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider cursor-pointer group" onClick={() => handleSort('employee_id')}>
                        <div className="flex items-center gap-2">ID <ArrowUpDown size={12} className="opacity-0 group-hover:opacity-100 transition" /></div>
                      </th>
                      <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider cursor-pointer group" onClick={() => handleSort('name')}>
                        <div className="flex items-center gap-2">Employee <ArrowUpDown size={12} className="opacity-0 group-hover:opacity-100 transition" /></div>
                      </th>
                      <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider">Bank Details</th>
                      <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider">Account Name</th>
                      <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider">Account No.</th>
                      <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider">IFSC / PAN</th>
                      <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-right">Base Salary</th>
                      <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-right">Bonus</th>
                      <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {currentEmployees.map((emp) => (
                      <tr key={emp.employee_id} className="hover:bg-white/5 transition">
                        <td className="p-4 text-sm text-white/60">{emp.employee_id}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <EmployeeAvatar emp={emp} />
                            <p className="text-sm font-semibold text-white">{emp.name || "Unknown"}</p>
                          </div>
                        </td>
                        <td className="p-4 text-sm">
                          <p className="text-white font-medium">{emp.bank_name || <span className="text-white/50 italic text-[11px]">Not Set</span>}</p>
                        </td>
                        <td className="p-4 text-sm">
                          <p className="text-white font-medium">{emp.account_name || <span className="text-white/50 italic text-[11px]">Not Set</span>}</p>
                        </td>
                        <td className="p-4 text-sm">
                          {emp.account_number ? (
                            <CopyableValue
                              label="account number"
                              value={emp.account_number}
                              copyKey={`${emp.employee_id}-account`}
                              className="max-w-[180px] text-white font-mono"
                            />
                          ) : (
                            <span className="text-white/50 italic text-[11px]">Not Set</span>
                          )}
                        </td>
                        <td className="p-4 text-sm">
                          <div className="space-y-1">
                            <p className="text-[11px] text-white/70 leading-none flex items-center gap-1">
                              <span>IFSC:</span>
                              <CopyableValue
                                label="IFSC"
                                value={emp.ifsc_code}
                                copyKey={`${emp.employee_id}-ifsc`}
                                className="max-w-[150px] text-white/80"
                              />
                            </p>
                            <p className="text-[11px] text-white/70 leading-none flex items-center gap-1">
                              <span>PAN:</span>
                              <CopyableValue
                                label="PAN"
                                value={emp.pan_number}
                                copyKey={`${emp.employee_id}-pan`}
                                className="max-w-[150px] text-white/80"
                              />
                            </p>
                          </div>
                        </td>
                        <td className="p-4 text-sm font-black text-right text-green-400">
                          ₹{formatCurrency(emp.salary)}
                        </td>
                        <td className="p-4 text-sm font-black text-right text-blue-400">
                          ₹{formatCurrency(bonuses.find(b => b.name === emp.name)?.monthly_amount || 0)}
                        </td>
                        {/* <td className="p-4">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => {
                                setEditingEmployee(emp);
                                setIsEditModalOpen(true);
                              }}
                              className="p-2 hover:bg-yellow-600/10 rounded-lg bg-yellow-500/10 text-yellow-500 hover:text-yellow-400 transition"
                              title="Edit Payroll Details"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => {
                                const empBonus = bonuses.find(b => b.name === emp.name);
                                setSelectedBonusEmployee({
                                  ...emp,
                                  bonus_id: empBonus?.id,
                                  bonus_amount: empBonus?.monthly_amount
                                });
                                setBonusModalOpen(true);
                              }}
                              className={`p-2 rounded-lg transition ${bonuses.find(b => b.name === emp.name) ? 'text-blue-400 hover:bg-blue-400/10' : 'text-green-400 hover:bg-green-400/10'}`}
                              title={bonuses.find(b => b.name === emp.name) ? "Edit Bonus" : "Create Bonus"}
                            >
                              <Gift size={16} />
                            </button>
                            {bonuses.find(b => b.name === emp.name) && (
                              <button
                                onClick={() => handleDeleteBonus(bonuses.find(b => b.name === emp.name).id)}
                                className="p-2  bg-red-500/10 text-red-400  hover:bg-red-600/10 rounded-lg  hover:text-red-500 transition"
                                title="Delete Bonus"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        </td> */}

                        <td className="p-4">
                          <div className="flex items-center justify-center gap-2">

                            {/* EDIT */}
                            <Tooltip text="Edit Details">
                              <button
                                onClick={() => {
                                  setEditingEmployee(emp);
                                  setIsEditModalOpen(true);
                                }}
                                className="p-2 hover:bg-yellow-600/10 rounded-lg bg-yellow-500/10 text-yellow-500 hover:text-yellow-400 transition"
                              >
                                <Edit size={16} />
                              </button>
                            </Tooltip>

                            {/* BONUS */}
                            <Tooltip text={bonuses.find(b => b.name === emp.name) ? "Edit Bonus" : "Add Bonus"}>
                              <button
                                onClick={() => {
                                  const empBonus = bonuses.find(b => b.name === emp.name);
                                  setSelectedBonusEmployee({
                                    ...emp,
                                    bonus_id: empBonus?.id,
                                    bonus_amount: empBonus?.monthly_amount
                                  });
                                  setBonusModalOpen(true);
                                }}
                                className={`p-2 rounded-lg transition ${bonuses.find(b => b.name === emp.name)
                                  ? 'text-blue-400 hover:bg-blue-400/10'
                                  : 'text-green-400 hover:bg-green-400/10'
                                  }`}
                              >
                                <Gift size={16} />
                              </button>
                            </Tooltip>

                            {/* DELETE */}
                            {bonuses.find(b => b.name === emp.name) && (
                              <Tooltip text="Delete Bonus">
                                <button
                                  onClick={() => handleDeleteBonus(bonuses.find(b => b.name === emp.name).id)}
                                  className="p-2 bg-red-500/10 text-red-400 hover:bg-red-600/10 rounded-lg hover:text-red-500 transition"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </Tooltip>
                            )}

                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {currentEmployees.map((emp) => (
                  <div key={emp.employee_id} className="bg-white/5 border border-white/10 p-5 rounded-2xl hover:border-purple-500/50 transition space-y-4 group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <EmployeeAvatar emp={emp} sizeClass="w-10 h-10" textClass="text-sm" />
                        <div>
                          <h4 className="text-sm font-bold text-white group-hover:text-purple-400 transition">{emp.name || "Unknown"}</h4>
                          <p className="text-[10px] text-white/70">ID: {emp.employee_id}</p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3 pt-2 border-t border-white/5">
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] text-white/60 uppercase font-black">Bank</span>
                        <span className="text-xs font-medium text-white/80">{emp.bank_name || "N/A"}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] text-white/60 uppercase font-black">A/C Name</span>
                        <span className="text-xs font-medium text-white/80">{emp.account_name || "N/A"}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] text-white/60 uppercase font-black">Account</span>
                        <CopyableValue
                          label="account number"
                          value={emp.account_number}
                          copyKey={`${emp.employee_id}-grid-account`}
                          className="max-w-[160px] text-xs font-mono text-white/80"
                        />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] text-white/60 uppercase font-black">IFSC</span>
                        <CopyableValue
                          label="IFSC"
                          value={emp.ifsc_code}
                          copyKey={`${emp.employee_id}-grid-ifsc`}
                          className="max-w-[160px] text-xs text-white/80"
                        />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] text-white/60 uppercase font-black">PAN</span>
                        <CopyableValue
                          label="PAN"
                          value={emp.pan_number}
                          copyKey={`${emp.employee_id}-grid-pan`}
                          className="max-w-[160px] text-xs text-white/80"
                        />
                      </div>
                      <div className="flex justify-between items-center border-t border-white/5 pt-2">
                        <span className="text-[10px] text-white/60 uppercase font-black">Base Salary</span>
                        <span className="text-sm font-black text-green-400">₹{formatCurrency(emp.salary)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] text-white/60 uppercase font-black">Monthly Bonus</span>
                        <span className="text-sm font-black text-blue-400">₹{formatCurrency(bonuses.find(b => b.name === emp.name)?.monthly_amount || 0)}</span>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditingEmployee(emp);
                          setIsEditModalOpen(true);
                        }}
                        className="flex-1 py-2 bg-yellow-500/10 hover:bg-yellow-500/20 border border-yellow-500/20 rounded-xl transition text-[10px] font-bold flex items-center justify-center gap-2 text-yellow-400"
                      >
                        <Edit size={14} /> Edit Info
                      </button>
                      <button
                        onClick={() => {
                          const empBonus = bonuses.find(b => b.name === emp.name);
                          setSelectedBonusEmployee({
                            ...emp,
                            bonus_id: empBonus?.id,
                            bonus_amount: empBonus?.monthly_amount
                          });
                          setBonusModalOpen(true);
                        }}
                        className={`flex-1 py-2 border rounded-xl transition text-[10px] font-bold flex items-center justify-center gap-2 ${bonuses.find(b => b.name === emp.name) ? 'bg-blue-500/10 border-blue-500/20 text-blue-400' : 'bg-green-500/10 border-green-500/20 text-green-400'}`}
                        title={bonuses.find(b => b.name === emp.name) ? "Edit Bonus" : "Create Bonus"}
                      >
                        <Gift size={14} /> {bonuses.find(b => b.name === emp.name) ? "Edit Bonus" : "Create Bonus"}
                      </button>
                      {bonuses.find(b => b.name === emp.name) && (
                        <button
                          onClick={() => handleDeleteBonus(bonuses.find(b => b.name === emp.name).id)}
                          className="p-2 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl hover:bg-red-500/20 transition"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="bg-white/5 border border-white/10 rounded-2xl p-12 text-center">
              <div className="bg-white/5 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users size={32} className="text-white/50" />
              </div>
              <h3 className="text-lg font-bold text-white">No employees found</h3>
              <p className="text-white/70 text-sm mt-1">Try adjusting your search or filters</p>
            </div>
          )
        )}

        {/* PAGINATION FOR SALARIES */}
        {viewMode === "salaries" && filteredEmployees.length > itemsPerPage && (
          <div className="flex items-center justify-center gap-2 py-6">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="p-2 text-white/70 hover:text-white transition disabled:opacity-30"
            >
              <ChevronRight size={18} className="rotate-180" />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-8 h-8 rounded-lg text-sm font-bold transition ${currentPage === page
                  ? 'bg-purple-600 text-white'
                  : 'bg-white/5 text-white/60 hover:bg-white/10'
                  }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="p-2 text-white/70 hover:text-white transition disabled:opacity-30"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}

        {viewMode === "bonuses" && (
          /* BONUS LIST VIEW */
          <div className="space-y-4">
            <div className="bg-white/5 border border-white/10 rounded-3xl overflow-hidden backdrop-blur-xl">
              <table className="w-full text-left border-collapse min-w-[1000px]">
                <thead>
                  <tr className="bg-white/5 border-b border-white/10">
                    <th className="p-4 text-[10px] uppercase font-black text-white/70 tracking-wider">Employee Name</th>
                    <th className="p-4 text-[10px] uppercase font-black text-white/70 tracking-wider">Salary ID</th>
                    <th className="p-4 text-[10px] uppercase font-black text-white/70 tracking-wider text-center">Month</th>
                    <th className="p-4 text-[10px] uppercase font-black text-white/70 tracking-wider text-right">Bonus Amount</th>
                    <th className="p-4 text-[10px] uppercase font-black text-white/70 tracking-wider text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {bonuses.length > 0 ? bonuses.map((bonus) => (
                    <tr key={bonus.id} className="hover:bg-white/[0.02] transition">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-[10px] font-bold">
                            {(bonus.name || "U")[0]}
                          </div>
                          <p className="text-sm font-semibold text-white">{bonus.name}</p>
                        </div>
                      </td>
                      <td className="p-4 text-sm text-white/70 font-mono">{bonus.employee_salary_id}</td>
                      <td className="p-4 text-sm text-white/60 text-center uppercase font-bold">{bonus.month}</td>
                      <td className="p-4 text-sm font-black text-right text-blue-400">₹{formatCurrency(bonus.monthly_amount)}</td>
                      <td className="p-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => {
                              const emp = employees.find(e => e.name === bonus.name);
                              setSelectedBonusEmployee({
                                ...emp,
                                bonus_id: bonus.id,
                                bonus_amount: bonus.monthly_amount,
                                name: bonus.name,
                                employee_id: bonus.employee_salary_id
                              });
                              setBonusModalOpen(true);
                            }}
                            className="p-2 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition"
                            title="Edit Bonus"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteBonus(bonus.id)}
                            className="p-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition"
                            title="Delete Bonus"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="5" className="p-12 text-center text-white/70">
                        <Gift size={48} className="mx-auto mb-4 opacity-10" />
                        No bonuses recorded for this month
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ALREADY EXISTS MODAL */}
      {showExistsModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowExistsModal(false)} />
          <div className="relative bg-[#1B103F] border border-white/10 w-full max-w-md rounded-3xl p-8 shadow-2xl space-y-6 text-center transform transition-all">
            <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto">
              <AlertCircle size={40} className="text-yellow-500" />
            </div>
            <div className="space-y-2">
              <h3 className="text-2xl font-bold text-white">Payroll Already Processed</h3>
              <p className="text-white/50 text-sm leading-relaxed">
                Payroll for <span className="text-white font-bold">{new Date(selectedMonth).toLocaleString('default', { month: 'long', year: 'numeric' })}</span> has already been generated and processed.
              </p>
            </div>
            <div className="flex flex-col gap-3 pt-4">
              <button
                onClick={() => setShowExistsModal(false)}
                className="w-full bg-white/10 hover:bg-white/20 text-white py-3 rounded-xl font-bold transition border border-white/10"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <EditPayrollInfoModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        employee={editingEmployee}
        onUpdate={() => setRefreshTrigger(prev => prev + 1)}
      />

      <BonusModal
        isOpen={bonusModalOpen}
        onClose={() => setBonusModalOpen(false)}
        employee={selectedBonusEmployee}
        selectedMonth={selectedMonth}
        onUpdate={() => setRefreshTrigger(prev => prev + 1)}
      />
    </div>
  );
};

export default EmployeeSalaries;
